import { useCallback, useEffect, useMemo, useState } from 'react';
import { FormInputCommonProps } from './types';
import { MoneyInput } from './money-input';
import PercentIcon from '../../images/Percent.svg';
import { TextInput } from './text-input';
import { get, useFormContext } from 'react-hook-form';
import { ErrorMessage } from '@hookform/error-message';
import { twMerge } from 'tailwind-merge';
import { NumberFormatValues } from 'react-number-format';

const truncateToDecimals = (num: number, dec = 3) => {
  const calcDec = Math.pow(10, dec);
  return Math.trunc(num * calcDec) / calcDec;
};

const getPercentage = (total: number, value?: number | null) => {
  if (!value || !total) return undefined;
  const floatPercent = (value / total) * 100;
  const trunc = truncateToDecimals(floatPercent);
  return floatPercent % 1 === 0 ? floatPercent : trunc;
};

const getDownValue = (total: number, percent: number) => {
  if (isNaN(percent)) return;
  if (!percent || !total) return 0;
  return Math.ceil((percent / 100) * total);
};

interface DownPaymentInputProps extends FormInputCommonProps {
  total: number;
  defaultValue?: number;
  value?: number | null;
  loading?: boolean;
  onChange?: (
    val: number | null | undefined,
    e: React.FocusEvent<HTMLInputElement, Element>
  ) => void;
  inputClassName?: string;
  leftIconClassName?: string;
  variant?: 'underline' | 'default';
  showBorder?: boolean;
}

export const DownPaymentInput: React.FC<DownPaymentInputProps> = ({
  total,
  value,
  disabled,
  loading,
  onChange,
  name,
  variant = 'default',
  inputClassName,
  leftIconClassName,
  showBorder = true,
}) => {
  const methods = useFormContext();
  const errors = methods?.formState.errors;
  const error = methods && name ? get(errors, name) : null;

  const defaultPercentage = useMemo(
    () => getPercentage(total, value),
    [total, value]
  );
  const [valueState, setValueState] = useState<{
    downPayment?: number | null;
    percentage?: number | null;
  }>({ downPayment: value, percentage: defaultPercentage });

  // Input A onChange
  // // set local total value

  const onChangeDownPayment = useCallback(
    (val?: number) => {
      // const newVal = val || 0;
      const newVal = val;
      setValueState((prev) => ({
        downPayment: newVal,
        percentage: getPercentage(total, newVal),
      }));
    },
    [total, setValueState]
  );

  // Input A onBlur
  // // if different from local -> upper onChange
  // // if same as local, focus Input B

  const onBlurDownPayment = useCallback(
    (e: React.FocusEvent<HTMLInputElement, Element>) => {
      if (valueState.downPayment !== value) {
        onChange?.(valueState.downPayment, e);
      }
    },
    [valueState, onChange]
  );

  // Input B onChange
  // // set local pct value

  // const onChangePercentage = useCallback(
  //   (e: React.ChangeEvent<HTMLInputElement>) => {
  //     let { value } = e.currentTarget;

  //     if (value != '') {
  //       // If possible, parse the current value to a float.
  //       let pVal = parseFloat(value);
  //       // ...and ensure it's within range
  //       if (pVal <= 0) pVal = 0;
  //       if (pVal > 100) pVal = 100;
  //       setValueState((prev) => ({ ...prev, percentage: pVal }));
  //     } else {
  //       setValueState((prev) => ({ ...prev, percentage: Number(value) }));
  //     }
  //   },
  //   [setValueState]
  // );

  const onChangePercentage = useCallback(
    (values: NumberFormatValues) => {
      const value = values.formattedValue;
      // let { value } = e.currentTarget;

      if (value != '') {
        // If possible, parse the current value to a float.
        let pVal = parseFloat(value);
        // ...and ensure it's within range
        if (pVal <= 0) pVal = 0;
        if (pVal > 100) pVal = 100;
        setValueState((prev) => ({ ...prev, percentage: pVal }));
      } else {
        // setValueState((prev) => ({ ...prev, percentage: Number(value) }));
        setValueState((prev) => ({ ...prev, percentage: null }));
      }
    },
    [setValueState]
  );

  // Input B onBlur
  // // if different from local -> upper onChange
  // // if same as local, let leave

  const onBlurPercentage = useCallback(
    (e: React.FocusEvent<HTMLInputElement, Element>) => {
      if (valueState.percentage !== defaultPercentage) {
        const newDownPayment = getDownValue(total, valueState.percentage || 0);
        onChange?.(newDownPayment, e);
      }
    },
    [valueState, total, onChange]
  );

  useEffect(() => {
    setValueState({ downPayment: value, percentage: defaultPercentage });
  }, [value, setValueState, total]);

  return (
    <>
      <div
        className={twMerge(
          'min-w-auto relative flex rounded-[4px] lg:min-w-[180px]',
          error
            ? 'border-[1px] border-hydrant-500 bg-hydrant-30'
            : showBorder
              ? 'border-[1px] border-hardwood-300 bg-white'
              : 'bg-white',
          inputClassName ? inputClassName : ''
        )}
      >
        <MoneyInput
          className={twMerge(
            'w-[60%] border-0 pr-0 bg-inherit',
            variant === 'underline' ? 'bg-transparent flex items-center' : ''
          )}
          disabled={loading || disabled}
          value={valueState.downPayment}
          onValueChange={(val) => {
            onChangeDownPayment(val.floatValue);
          }}
          onBlur={onBlurDownPayment}
          fieldErrorOverride={error}
          inputClassName="bg-inherit"
          leftIconClassName={variant === 'underline' ? 'pl-0' : ''}
          variant={variant}
        />
        <MoneyInput
          className={twMerge(
            'w-[40%] border-0 text-right pl-0 bg-inherit',
            variant === 'underline' ? 'bg-transparent flex items-center' : ''
          )}
          disabled={loading || disabled}
          value={valueState.percentage || 0}
          onValueChange={onChangePercentage}
          onBlur={onBlurPercentage}
          inputClassName={twMerge(
            'text-right pl-0 bg-inherit',
            variant === 'underline' ? 'pr-[4px]' : ''
          )}
          hideMoneyIcon={true}
          rightIcon={<img src={PercentIcon} />}
          fieldErrorOverride={error}
          rightIconClassName={variant === 'underline' ? 'pr-0' : ''}
          variant={variant}
        />
      </div>
      {name && error && variant !== 'underline' ? (
        <ErrorMessage
          errors={errors}
          name={name}
          render={({ message }) => {
            return (
              <div className="typo-body-small text-hydrant-500 absolute">
                {message}
              </div>
            );
          }}
        />
      ) : null}
    </>
  );
};
